import React, { useEffect } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  useColorMode,
  Icon,
  Flex,
} from '@chakra-ui/react'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  FaChartLine,
  FaUsers,
  FaLaptop,
  FaClock,
  FaShieldAlt,
  FaTrophy,
  FaGraduationCap,
  FaRocket,
  FaCheckCircle,
} from 'react-icons/fa'
import LEColorConstants, {
  basicTextColor,
} from '@/lib/constants/colorConstants'

const benefits = [
  {
    icon: FaLaptop,
    title: 'Familiar CBT Interface',
    description:
      "Students practice with the exact same interface they'll face in WAEC, NECO, and JAMB exams.",
    color: LEColorConstants.primary,
  },
  {
    icon: FaChartLine,
    title: 'Real-Time Analytics',
    description:
      'Track individual student progress, identify weak areas, and monitor overall class performance instantly.',
    color: LEColorConstants.secondary,
  },
  {
    icon: FaUsers,
    title: 'Unlimited Students',
    description:
      'Test your entire school population without worrying about any limitations.',
    color: LEColorConstants.colorCheck,
  },
  {
    icon: FaClock,
    title: 'Time Management Training',
    description:
      'Students learn crucial time management skills with realistic exam timing and automatic submissions.',
    color: '#8B5CF6',
  },
  {
    icon: FaShieldAlt,
    title: 'Exam Security Features',
    description:
      'Advanced anti-cheating measures including screen monitoring and randomized question orders.',
    color: '#EF4444',
  },
  {
    icon: FaTrophy,
    title: 'Competitive Edge',
    description:
      'Give your students an unfair advantage with extensive practice before their competitors even start.',
    color: '#F59E0B',
  },
]

const outcomes = [
  {
    icon: FaGraduationCap,
    title: '40% Higher Pass Rates',
    description:
      'Schools using CBT practice see significantly improved exam performance',
    metric: '+40%',
  },
  {
    icon: FaRocket,
    title: '85% Faster Preparation',
    description: 'Reduce exam preparation time while increasing effectiveness',
    metric: '85%',
  },
  {
    icon: FaCheckCircle,
    title: '99.9% System Reliability',
    description:
      'Never worry about system downtime during critical exam periods',
    metric: '99.9%',
  },
]

export const BenefitsSection = () => {
  const { colorMode } = useColorMode()
  const bgColor = useColorModeValue('white', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')

  const controls = useAnimation()
  const { inView, ref } = useInView({ threshold: 0.2 })

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  return (
    <Box
      bg={bgColor}
      py="5rem"
      ref={ref}
      as={motion.div}
      animate={controls}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <Container maxW="90rem" mx="auto" px={['1rem', '3rem', '4rem', '6rem']}>
        <VStack spacing="4rem">
          {/* Header */}
          <Box
            textAlign="center"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            <Heading
              fontSize={['2rem', '2.5rem', '3rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="1rem"
            >
              Why Top Schools Choose{' '}
              <Text as="span" color={LEColorConstants.primary}>
                Our CBT Platform
              </Text>
            </Heading>

            <Text
              fontSize={['1.1rem', '1.3rem']}
              color={basicTextColor[colorMode]}
              maxW="4xl"
              mx="auto"
              fontWeight="medium"
            >
              Transform your students into confident, exam-ready candidates who
              excel in any testing environment
            </Text>
          </Box>

          {/* Key Benefits Grid */}
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing="2rem"
            w="full"
            as={motion.div}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 0.2,
                },
              },
            }}
          >
            {benefits.map((benefit, index) => (
              <Box
                key={index}
                bg={cardBg}
                p="2rem"
                borderRadius="16px"
                boxShadow="lg"
                border="1px solid"
                borderColor="gray.200"
                position="relative"
                overflow="hidden"
                role="group"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: '2xl',
                  borderColor: benefit.color,
                }}
                transition="all 0.3s ease"
                as={motion.div}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                }}
              >
                {/* Background Gradient */}
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  h="4px"
                  bg={benefit.color}
                />

                <VStack align="start" spacing="1rem">
                  <Flex
                    w="60px"
                    h="60px"
                    bg={`${benefit.color}15`}
                    borderRadius="12px"
                    align="center"
                    justify="center"
                    _groupHover={{
                      bg: benefit.color,
                    }}
                    transition="all 0.3s ease"
                  >
                    <Icon
                      as={benefit.icon}
                      boxSize="24px"
                      color={benefit.color}
                      _groupHover={{
                        color: 'white',
                      }}
                      transition="all 0.3s ease"
                    />
                  </Flex>

                  <Heading
                    size="md"
                    color={basicTextColor[colorMode]}
                    fontWeight="bold"
                  >
                    {benefit.title}
                  </Heading>

                  <Text
                    color={basicTextColor[colorMode]}
                    fontSize="1rem"
                    lineHeight="1.6"
                  >
                    {benefit.description}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>

          {/* Outcomes Section */}
          <Box w="full" textAlign="center">
            <Heading
              fontSize={['1.8rem', '2.2rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="3rem"
              as={motion.div}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.6, delay: 0.4 },
                },
              }}
            >
              Proven Results That Speak for Themselves
            </Heading>

            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing="2rem"
              as={motion.div}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.2,
                    delayChildren: 0.6,
                  },
                },
              }}
            >
              {outcomes.map((outcome, index) => (
                <Box
                  key={index}
                  bg="linear-gradient(135deg, #009BFF10 0%, #FF6E0610 100%)"
                  p="2.5rem"
                  borderRadius="20px"
                  border="2px solid"
                  borderColor={
                    index === 0
                      ? LEColorConstants.primary
                      : index === 1
                      ? LEColorConstants.secondary
                      : LEColorConstants.colorCheck
                  }
                  textAlign="center"
                  as={motion.div}
                  variants={{
                    hidden: { opacity: 0, scale: 0.9 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: { duration: 0.5 },
                    },
                  }}
                  whileHover={{
                    scale: 1.05,
                    transition: { duration: 0.2 },
                  }}
                >
                  <Icon
                    as={outcome.icon}
                    boxSize="48px"
                    color={
                      index === 0
                        ? LEColorConstants.primary
                        : index === 1
                        ? LEColorConstants.secondary
                        : LEColorConstants.colorCheck
                    }
                    mb="1rem"
                  />

                  <Text
                    fontSize="3rem"
                    fontWeight="bold"
                    color={
                      index === 0
                        ? LEColorConstants.primary
                        : index === 1
                        ? LEColorConstants.secondary
                        : LEColorConstants.colorCheck
                    }
                    lineHeight="1"
                  >
                    {outcome.metric}
                  </Text>

                  <Heading
                    size="md"
                    color={basicTextColor[colorMode]}
                    mb="0.5rem"
                    mt="1rem"
                  >
                    {outcome.title}
                  </Heading>

                  <Text color={basicTextColor[colorMode]} fontSize="1rem">
                    {outcome.description}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* Feature Highlight */}
          <Box
            w="full"
            bg="linear-gradient(135deg, #009BFF 0%, #FF6E06 100%)"
            borderRadius="20px"
            p="3rem"
            textAlign="center"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.8 },
              },
            }}
          >
            <Heading
              fontSize={['1.8rem', '2.2rem']}
              fontWeight="bold"
              color="white"
              mb="1rem"
            >
              🎯 The Complete CBT Preparation Solution
            </Heading>

            <Text
              fontSize={['1.1rem', '1.3rem']}
              color="white"
              maxW="4xl"
              mx="auto"
              mb="2rem"
              lineHeight="1.6"
            >
              From mock exams to performance analytics, we provide everything
              your school needs to ensure 100% CBT readiness for every single
              student.
            </Text>

            <HStack justify="center" spacing="2rem" flexWrap="wrap">
              {[
                '✅ Question Banks',
                '✅ Mock Exams',
                '✅ Progress Tracking',
                '✅ Performance Reports',
              ].map((feature, index) => (
                <Text
                  key={index}
                  color="white"
                  fontSize={['0.9rem', '1.1rem']}
                  fontWeight="bold"
                >
                  {feature}
                </Text>
              ))}
            </HStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
